import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import CardChartTotal from '@/components/ui/card-wrapper/CardChartTotal';
import { Tables } from '@/types/database.types';
import { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import { Upload, Download, Trash2, HardDrive, FileIcon, FolderSync } from 'lucide-react';

type StorageLogDashboardProps = {
  data: Tables<'storage_log'>[];
  showProjectChart?: boolean;
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const OperationsByDateChart = ({ data }: { data: Tables<'storage_log'>[] }) => {
  const chartData = useMemo(() => {
    // Get last 7 days
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      return dayjs().subtract(6 - i, 'day').startOf('day').valueOf();
    });

    // Initialize all 7 days with zero values
    const grouped = last7Days.reduce(
      (acc, dayKey) => {
        acc[dayKey] = {
          date: dayKey,
          upload: 0,
          download: 0,
          delete: 0,
          copy: 0,
          move: 0,
          list: 0,
          other: 0,
        };
        return acc;
      },
      {} as Record<number, { date: number; upload: number; download: number; delete: number; copy: number; move: number; list: number; other: number }>
    );

    // Fill in actual data
    data.forEach((log) => {
      const dayKey = dayjs(log.created_at).startOf('day').valueOf();
      if (grouped[dayKey]) {
        const opType = log.operation_type.toLowerCase();
        if (opType in grouped[dayKey]) {
          grouped[dayKey][opType as keyof typeof grouped[typeof dayKey]]++;
        } else {
          grouped[dayKey].other++;
        }
      }
    });

    return Object.values(grouped).sort((a, b) => a.date - b.date);
  }, [data]);

  const series: ApexAxisChartSeries = [
    { name: 'Upload', data: chartData.map((item) => item.upload), color: 'hsl(var(--chart-1))' },
    { name: 'Download', data: chartData.map((item) => item.download), color: 'hsl(var(--chart-2))' },
    { name: 'Delete', data: chartData.map((item) => item.delete), color: 'hsl(var(--chart-5))' },
    { name: 'Copy', data: chartData.map((item) => item.copy), color: 'hsl(var(--chart-3))' },
    { name: 'Move', data: chartData.map((item) => item.move), color: 'hsl(var(--chart-4))' },
    { name: 'List', data: chartData.map((item) => item.list), color: 'hsl(var(--chart-6))' },
  ];

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      height: 300,
      stacked: true,
      toolbar: { show: true, tools: { download: false } },
      zoom: { enabled: true },
    },
    legend: { markers: { shape: 'circle' } },
    responsive: [
      {
        breakpoint: 480,
        options: { legend: { position: 'bottom', offsetX: -10, offsetY: 0 } },
      },
    ],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
        borderRadius: 2,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
        dataLabels: {
          hideOverflowingLabels: true,
          total: {
            enabled: true,
            formatter: function (total) {
              if (!total) return '';
              return String(total);
            },
          },
        },
      },
    },
    xaxis: {
      type: 'datetime',
      categories: chartData.map((item) => item.date),
      labels: { datetimeUTC: false },
    },
    fill: { opacity: 1 },
    dataLabels: { enabled: false },
    tooltip: { shared: true, intersect: false },
  };

  return chartData.length > 0 ? (
    <Chart options={options} series={series} type="bar" height={300} />
  ) : (
    <div className="text-center text-sm text-muted-foreground py-8">No data</div>
  );
};

const OperationsByProjectChart = ({ data }: { data: Tables<'storage_log'>[] }) => {
  const chartData = useMemo(() => {
    const grouped = data.reduce(
      (acc, log) => {
        const projectId = log.project_id.slice(0, 8);
        if (!acc[projectId]) {
          acc[projectId] = {
            project: projectId,
            upload: 0,
            download: 0,
            delete: 0,
            other: 0,
          };
        }
        const opType = log.operation_type.toLowerCase();
        if (opType === 'upload') acc[projectId].upload++;
        else if (opType === 'download') acc[projectId].download++;
        else if (opType === 'delete') acc[projectId].delete++;
        else acc[projectId].other++;
        return acc;
      },
      {} as Record<string, { project: string; upload: number; download: number; delete: number; other: number }>
    );
    return Object.values(grouped)
      .sort((a, b) => (b.upload + b.download + b.delete + b.other) - (a.upload + a.download + a.delete + a.other))
      .slice(0, 10);
  }, [data]);

  const series: ApexAxisChartSeries = [
    { name: 'Upload', data: chartData.map((item) => item.upload), color: 'hsl(var(--chart-1))' },
    { name: 'Download', data: chartData.map((item) => item.download), color: 'hsl(var(--chart-2))' },
    { name: 'Delete', data: chartData.map((item) => item.delete), color: 'hsl(var(--chart-5))' },
    { name: 'Other', data: chartData.map((item) => item.other), color: 'hsl(var(--chart-4))' },
  ];

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      height: 300,
      stacked: true,
      toolbar: { show: true, tools: { download: false } },
    },
    legend: { markers: { shape: 'circle' } },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '50%',
        borderRadius: 2,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    xaxis: {
      categories: chartData.map((item) => item.project + '...'),
    },
    fill: { opacity: 1 },
    dataLabels: { enabled: false },
    tooltip: { shared: true, intersect: false },
  };

  return chartData.length > 0 ? (
    <Chart options={options} series={series} type="bar" height={300} />
  ) : (
    <div className="text-center text-sm text-muted-foreground py-8">No data</div>
  );
};

const StorageLogDashboard = ({ data, showProjectChart = true }: StorageLogDashboardProps) => {
  const stats = useMemo(() => {
    const operationCounts = data.reduce(
      (acc, log) => {
        const op = log.operation_type.toLowerCase();
        acc[op] = (acc[op] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const totalSize = data.reduce((acc, log) => acc + (log.file_size_bytes || 0), 0);
    const totalFiles = data.reduce((acc, log) => acc + (log.file_count || 0), 0);
    const uniqueProjects = new Set(data.map((log) => log.project_id)).size;

    return {
      total: data.length,
      uploads: operationCounts['upload'] || 0,
      downloads: operationCounts['download'] || 0,
      deletes: operationCounts['delete'] || 0,
      totalSize,
      totalFiles,
      uniqueProjects,
    };
  }, [data]);

  return (
    <div className="space-y-4 mb-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Operations</CardTitle>
            <FolderSync className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total.toLocaleString()}</div>
            {showProjectChart && (
              <p className="text-xs text-muted-foreground">
                across {stats.uniqueProjects} project{stats.uniqueProjects !== 1 ? 's' : ''}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uploads</CardTitle>
            <Upload className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uploads.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {stats.total > 0 ? ((stats.uploads / stats.total) * 100).toFixed(1) : 0}% of operations
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Downloads</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.downloads.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {stats.total > 0 ? ((stats.downloads / stats.total) * 100).toFixed(1) : 0}% of operations
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Data</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatBytes(stats.totalSize)}</div>
            <p className="text-xs text-muted-foreground">
              {stats.totalFiles.toLocaleString()} files processed
            </p>
          </CardContent>
        </Card>
      </div>

      <div className={`grid gap-4 ${showProjectChart ? 'md:grid-cols-2' : ''}`}>
        <CardChartTotal title="Operations (Last 7 Days)" description="Storage operations grouped by date and type">
          <OperationsByDateChart data={data} />
        </CardChartTotal>
        {showProjectChart && (
          <CardChartTotal title="Operations by Project" description="Top 10 projects by operation count">
            <OperationsByProjectChart data={data} />
          </CardChartTotal>
        )}
      </div>
    </div>
  );
};

export default StorageLogDashboard;
